import { reactive, ref } from 'vue';
import { apiPath, type PresenceBatchEvent, PresenceSnapshotSchema, SCREEN_OFFLINE_AFTER_MS, SocketEvents } from '@codot/contracts';
import { acquireCmsSocket } from '../api/realtime';
import { getJson } from '../api/http';

/**
 * Live connection status for every place the CMS shows a player (lists, detail page, publish drawer, group, tag
 * and region panels, maps). ONE snapshot per tab, then the server's batched updates on the shared socket — opening
 * more pages adds no requests.
 *
 * Connected → online at once. Connection dropped → "reconnecting" (players in lifts drop for a minute many times a
 * day and keep playing); "offline" only once nothing has been heard for SCREEN_OFFLINE_AFTER_MS, as before.
 */
const connected = reactive(new Set<string>());
const droppedAt = reactive(new Map<string, number>());
const loaded = ref(false);
/** Ticks so "reconnecting" turns into "offline" on screen without anything else changing. */
export const presenceNow = ref(Date.now());

let started = false;
let load: () => Promise<void> = async () => undefined;
/**
 * Start following, or (already started) reload for another organization. Called by the signed-in shell, and
 * again when the active organization changes.
 */
export function startPresence(): void {
  if (started) return void load();
  started = true;
  const socket = acquireCmsSocket(); // kept for the tab's lifetime
  load = async () => {
    try {
      const snap = await getJson(apiPath('presence'), PresenceSnapshotSchema);
      connected.clear();
      for (const id of snap.connected) connected.add(id);
      droppedAt.clear();
      for (const [id, at] of Object.entries(snap.droppedAt)) droppedAt.set(id, Date.parse(at));
      loaded.value = true;
    } catch {
      /* the heartbeat rule still applies */
    }
  };
  // On every (re)connect of this tab: anything may have changed while it was away.
  socket.on('connect', () => void load());
  if (socket.connected) void load();
  socket.on('disconnect', () => (loaded.value = false));
  socket.on(SocketEvents.SCREEN_PRESENCE_BATCH, (e: PresenceBatchEvent) => {
    for (const c of e.changes) {
      if (c.connected) {
        connected.add(c.screenId);
        droppedAt.delete(c.screenId);
      } else {
        connected.delete(c.screenId);
        droppedAt.set(c.screenId, Date.parse(c.at));
      }
    }
  });
  setInterval(() => (presenceNow.value = Date.now()), 10_000);
}

export type Connection = 'online' | 'reconnecting' | 'offline';

/** A paired player that has been seen: online, reconnecting (connection dropped, not long silent) or offline. */
export function connectionOf(screenId: string, lastSeenAt: string | null, now = presenceNow.value): Connection {
  const recent = !!lastSeenAt && now - Date.parse(lastSeenAt) < SCREEN_OFFLINE_AFTER_MS;
  if (!loaded.value) return recent ? 'online' : 'offline';
  if (connected.has(screenId)) return 'online';
  if (!recent) return 'offline';
  // Its connection dropped: back to "online" only by reconnecting (heartbeats alone don't say it's reachable).
  return droppedAt.has(screenId) ? 'reconnecting' : 'online';
}
export const isOnline = (screenId: string, lastSeenAt: string | null, now?: number) => connectionOf(screenId, lastSeenAt, now) === 'online';
